import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { AgentThreadRecord } from "../AgentThreadRecord";

interface UseAgentThreadsForUserOptions {
  pageToken?: string;
}

/**
 * React Query hook for fetching a paginated list of threads for the current user.
 *
 * @param options - Optional configuration including pageToken for pagination
 * @returns Query object with thread list data, loading state, and error state
 *
 * @example
 * ```tsx
 * const agentThreadsQuery = useAgentThreadsForUser();
 *
 * if (agentThreadsQuery.isLoading) {
 *   return <div>Loading threads...</div>;
 * }
 *
 * if (agentThreadsQuery.error) {
 *   return <div>Error loading threads</div>;
 * }
 *
 * return (
 *   <div>
 *     {agentThreadsQuery.data?.items.map(thread => (
 *       <div key={thread.thread_id}>{thread.title}</div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useAgentThreadsForUser(
  options?: UseAgentThreadsForUserOptions,
) {
  const { agentThreadService } = useDomainServices();

  return useQuery<PaginatedResponse<AgentThreadRecord>, Error>({
    queryKey: ["agentThreadsForUser", options?.pageToken],
    queryFn: ({ signal }) => {
      return agentThreadService.getAgentThreadsForUser({
        pageToken: options?.pageToken,
        abortSignal: signal,
      });
    },
    // Keep thread list relatively fresh
    staleTime: 30000, // 30 seconds
  });
}
